import type { Filter, Sprite } from 'pixi.js';
import { HeroRevealFilter } from './hero-reveal-filter';
import { heroRevealBounds, heroRevealEllipse, campRevealEllipse, campRevealTargets, ownedOccludedTargets, ownedRevealTargets } from './hero-reveal-geometry';
import type { GameTextureManifestEntry } from '@/game/assets/resolveGameAssetUrl';
import { spritePresentation } from '@/game/assets/sprite-presentation';
export type BodyRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};
export type VisibilitySprite = {
  x: number;
  y: number;
  zIndex: number;
  label?: string;
  visible: boolean;
  alpha: number;
  texture: {
    width: number;
    height: number;
  };
  filters?: readonly Filter[] | null;
  destroyed?: boolean;
  anchor: {
    x: number;
    y: number;
  };
  scale: {
    x: number;
    y: number;
  };
};
export type VisibilityEntry = {
  sprite: VisibilitySprite;
  textureKey: string;
  bodyRect?: BodyRect;
};
type RevealState = {
  filter: HeroRevealFilter;
  attached: boolean;
  baseFilters: readonly Filter[] | null | undefined;
};
/** Artwork body, excluding transparent margins; stays in world-container coordinates. */
export function spriteBodyRect(sprite: VisibilitySprite, entry?: GameTextureManifestEntry): BodyRect {
  const { width, height } = sprite.texture;
  const body = spritePresentation(entry, width, height) ? entry?.presentation?.bodyBoundsPx : undefined;
  const x = body?.x ?? 0, y = body?.y ?? 0;
  const w = body?.width ?? width, h = body?.height ?? height;
  const left = sprite.x + (x - sprite.anchor.x * width) * sprite.scale.x;
  const top = sprite.y + (y - sprite.anchor.y * height) * sprite.scale.y;
  const right = left + w * sprite.scale.x, bottom = top + h * sprite.scale.y;
  return { left: Math.min(left, right), top: Math.min(top, bottom),
    right: Math.max(left, right), bottom: Math.max(top, bottom) };
}
export function rectanglesOverlap(a: BodyRect, b: BodyRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}
/** Local reveal effects are attached only to foreground objects occluding the owned hero. */
export class ActorVisibility {
  private readonly cached = new Map<VisibilitySprite, RevealState>();
  private detach(sprite: VisibilitySprite, state: RevealState): void {
    if (!state.attached)
      return;
    if (!sprite.destroyed) {
      const remaining = sprite.filters?.filter(f => f !== state.filter) ?? [];
      const base = state.baseFilters ?? [];
      sprite.filters = remaining.length === base.length && remaining.every((f, i) => f === base[i]) ? state.baseFilters : remaining;
    }
    state.attached = false;

  }
  clear(): void {
    for (const [sprite, state] of this.cached)
      this.detach(sprite, state);
  }
  dispose(): void {
    this.clear();
    for (const state of this.cached.values())
      state.filter.destroy(false);
    this.cached.clear();
  }
  update(actors: Iterable<VisibilityEntry>, occluders: Iterable<VisibilityEntry>, metadata: (key: string) => GameTextureManifestEntry | undefined, viewport: BodyRect, ownHero?: VisibilitySprite, camp: Iterable<VisibilityEntry> = []): void {
    const desired = new Set<VisibilitySprite>();
    const heroEntry = ownHero ? [...actors].find(actor => actor.sprite === ownHero) : undefined;
    if (ownHero && heroEntry) {
      const campEntries = [...camp].filter(entry=>entry.sprite.visible && entry.sprite.alpha > 0 && !entry.sprite.destroyed);
      const walkBody = heroEntry.bodyRect ?? spriteBodyRect(ownHero, metadata(heroEntry.textureKey));
      const targets = ownedRevealTargets(ownHero, walkBody, campEntries.map(entry => ({ sprite: entry.sprite,
        body: entry.bodyRect ?? spriteBodyRect(entry.sprite, metadata(entry.textureKey)) })), viewport);
      const campVisible = targets.some(target=>target.sprite !== ownHero);
      const protectedCamp = campVisible ? campRevealTargets(ownHero,targets) : [];
      const ellipse = campVisible ? campRevealEllipse(ownHero) : heroRevealEllipse(ownHero,walkBody);
      const revealBounds=heroRevealBounds(ownHero,ellipse);
      for (const { sprite, textureKey, bodyRect } of occluders) {
        if (!sprite.visible || sprite.alpha <= 0 || sprite.destroyed) continue;
        const rect = bodyRect ?? spriteBodyRect(sprite, metadata(textureKey));
        if (rect.bottom - rect.top < 60 || !rectanglesOverlap(rect, viewport)) continue;
        const eligible = campVisible ? ownedOccludedTargets(protectedCamp,sprite,revealBounds) : [];
        const inFront = sprite.zIndex > ownHero.zIndex || (sprite.zIndex === ownHero.zIndex && (sprite.label ?? '') > (ownHero.label ?? ''));
        if (!rectanglesOverlap(rect,revealBounds) || (campVisible ? !eligible.length : !targets.length || !inFront)) continue;
        desired.add(sprite);
        let state = this.cached.get(sprite);
        if (!state) {
          state = { filter: new HeroRevealFilter(ownHero as Sprite), attached: false, baseFilters: undefined };
          this.cached.set(sprite, state);
        }
        state.filter.setHero(ownHero as Sprite, ellipse);
        if (!state.attached || !sprite.filters?.includes(state.filter)) {
          state.baseFilters = sprite.filters;
          sprite.filters = [...(sprite.filters ?? []), state.filter];
          state.attached = true;
        }
      }
    }
    for (const [sprite, state] of this.cached) {
      if (!desired.has(sprite))
        this.detach(sprite, state);
      if (sprite.destroyed) {
        state.filter.destroy(false);
        this.cached.delete(sprite);
      }
    }
  }
}
